import React, { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ReplyIcon from "@mui/icons-material/Reply";
import ForwardIcon from "@mui/icons-material/Forward";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddReactionOutlinedIcon from "@mui/icons-material/AddReactionOutlined";
import { useTranslation } from "react-i18next";
import TeamPresenceAvatar from "../../../components/TeamPresenceAvatar";
import TeamMessageAttachment from "./TeamMessageAttachment";
import TeamChatbotInteractive from "./TeamChatbotInteractive";
import TeamQuotedBlock from "./TeamQuotedBlock";
import TeamMessageReactions from "./TeamMessageReactions";
import TeamReactionPicker from "./TeamReactionPicker";
import VideoInviteMessage from "../../../components/MessageHistory/VideoInviteMessage";
import VideoCallSummaryMessage, {
  resolveVideoCallSummary,
  resolveVideoCallRecording,
  resolveVideoCallTranscript,
} from "../../../components/MessageHistory/VideoCallSummaryMessage";
import { resolveVideoInviteFromMessage } from "../../../utils/videoInviteMessage";
import { getChatbotInteractiveLabels } from "../../../utils/chatbotQuickReplies";
import { formatMessageTime, isSameAuthorGroup, TEAM_MOBILE_MEDIA_QUERY } from "../utils/teamUiUtils";
import TeamMessageStatus from "./TeamMessageStatus";
import TeamSystemMessageText from "./TeamSystemMessageText";
import TeamLinkifiedText from "./TeamLinkifiedText";
import TeamYouTubeEmbed from "./TeamYouTubeEmbed";
import TeamMessageEditHistoryDialog from "./TeamMessageEditHistoryDialog";
import { messageHasEditHistory } from "../utils/teamEditHistoryUtils";
import { isOptimisticTeamMessageId } from "../utils/teamOptimisticMessage";
import { resolveTeamQuotedNavigationTarget } from "../utils/teamMessageUtils";
import { extractYouTubeVideosFromText, isYouTubeOnlyText } from "../../../utils/youtubeEmbed";

function MessageMetaFooter({
  msg,
  isOwn,
  spaceType,
  theme,
  t,
  onShowEditHistory,
  onLightSurface = false,
}) {
  const time = formatMessageTime(msg.createdAt);
  const metaColor =
    isOwn && !onLightSurface
      ? alpha(theme.palette.primary.contrastText, 0.82)
      : theme.palette.text.secondary;
  const showEdited = Boolean(msg.editedAt && !msg.deletedAt);

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        flexShrink: 0,
        ml: 1.25,
        verticalAlign: "bottom",
        lineHeight: 1,
        userSelect: "none",
      }}
    >
      {showEdited ? (
        <Typography
          component="span"
          role={onShowEditHistory ? "button" : undefined}
          tabIndex={onShowEditHistory ? 0 : undefined}
          onClick={
            onShowEditHistory
              ? (e) => {
                  e.stopPropagation();
                  onShowEditHistory();
                }
              : undefined
          }
          onKeyDown={
            onShowEditHistory
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onShowEditHistory();
                  }
                }
              : undefined
          }
          sx={{
            fontSize: "0.625rem",
            lineHeight: 1,
            fontWeight: 500,
            color: metaColor,
            opacity: 0.9,
            mr: 0.35,
            ...(onShowEditHistory
              ? {
                  cursor: "pointer",
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                  textUnderlineOffset: 2,
                  "&:hover": { opacity: 1 },
                }
              : {}),
          }}
        >
          {t("editedLabel")}
        </Typography>
      ) : null}
      <Typography
        component="span"
        sx={{
          fontSize: "0.6875rem",
          lineHeight: 1,
          fontWeight: 500,
          color: metaColor,
          letterSpacing: "0.01em",
        }}
      >
        {time}
      </Typography>
      {isOwn ? (
        <TeamMessageStatus message={msg} spaceType={spaceType} isOwn inBubble />
      ) : null}
    </Box>
  );
}

const TeamMessageBubble = ({
  msg,
  prev,
  currentUserId,
  isSending,
  onQuickReply,
  clearedInteractiveIds,
  onReply,
  onEdit,
  onDelete,
  onForward,
  onReact,
  spaceType = "channel",
  onAudioPlayed,
  onNavigateToQuotedMessage,
  currentSpaceId = null,
  spaceLeadId = null,
  spaceCardId = null,
  onOpenTask = null,
}) => {
  const { t } = useTranslation("team");
  const theme = useTheme();
  const isCompactActions = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [hovered, setHovered] = useState(false);
  const [editHistoryOpen, setEditHistoryOpen] = useState(false);
  const canViewEditHistory = messageHasEditHistory(msg);

  const isOwn = msg.authorType === "user" && String(msg.authorId) === String(currentUserId);
  const isDeleted = Boolean(msg.deletedAt);
  const isSystem = msg.authorType === "system" || msg.messageType === "system";
  const isAgent = msg.authorType === "agent";
  const isChatbot = msg.authorType === "chatbot";
  const grouped = !isDeleted && isSameAuthorGroup(prev, msg);
  const hasAttachment =
    !isDeleted &&
    (msg.imageUrl ||
      msg.videoUrl ||
      msg.audioUrl ||
      msg.fileUrl ||
      ["image", "video", "audio", "file"].includes(msg.messageType));
  const contentTrim = isDeleted ? "" : String(msg.content || "").trim();
  const youtubeVideos =
    !isDeleted && !isSystem ? extractYouTubeVideosFromText(msg.content) : [];
  const deletedPlaceholder = isOwn ? t("deletedMessageOwn") : t("deletedMessageOther");
  const interactiveLabels = isChatbot ? getChatbotInteractiveLabels(msg.metadata) : [];
  const showInteractive =
    isChatbot &&
    !clearedInteractiveIds?.has(msg.id) &&
    (interactiveLabels.length > 0 ||
      Boolean(msg.metadata?.interactiveOptions?.ctaUrl || msg.metadata?.interactiveOptions?.url));
  const videoInvite = !isDeleted && !isSystem ? resolveVideoInviteFromMessage(msg) : null;
  const videoCallSummary = !isDeleted ? resolveVideoCallSummary(msg) : null;
  const videoCallRecording = !isDeleted && !videoCallSummary ? resolveVideoCallRecording(msg) : null;
  const videoCallTranscript = !isDeleted && !videoCallSummary ? resolveVideoCallTranscript(msg) : null;
  // Convite com resumo anexado vira o card único pós-chamada (mesmo balão).
  const showVideoCallArtifact = Boolean(videoCallSummary || videoCallRecording || videoCallTranscript);
  const showVideoInvite = Boolean(videoInvite?.url) && !showVideoCallArtifact;
  const showYoutubeEmbeds = youtubeVideos.length > 0 && !showVideoCallArtifact;
  const youtubeOnly = showYoutubeEmbeds && isYouTubeOnlyText(contentTrim);
  const showText =
    isDeleted ||
    (Boolean(contentTrim) &&
      contentTrim !== String(msg.fileName || "").trim() &&
      !youtubeOnly);
  const isPendingOrFailed =
    msg?.sendStatus === "sending" ||
    msg?.sendStatus === "error" ||
    isOptimisticTeamMessageId(msg?.id);

  const canEdit =
    isOwn &&
    !isPendingOrFailed &&
    !isDeleted &&
    !isSystem &&
    !showVideoInvite &&
    !showVideoCallArtifact &&
    String(msg.messageType || "text") === "text" &&
    !msg.imageUrl &&
    !msg.videoUrl &&
    !msg.audioUrl &&
    !msg.fileUrl;
  const canDelete = isOwn && !isSystem && !isDeleted && !isPendingOrFailed;

  const authorLabel = isSystem
    ? t("systemMessage")
    : isAgent
      ? msg.authorName || t("agentMessage")
      : isChatbot
        ? msg.authorName || t("chatbotMessage")
        : msg.authorName || "—";

  const forwarded = msg.metadata?.forwardedFrom;
  const hasReactions = Array.isArray(msg.reactions) && msg.reactions.length > 0;
  const hasBubbleBody =
    (showText ||
      msg.replyPreview ||
      forwarded ||
      hasAttachment ||
      showVideoInvite ||
      showVideoCallArtifact ||
      showYoutubeEmbeds) &&
    !showInteractive;
  const hasMediaBelow = hasAttachment || showYoutubeEmbeds;
  const showAuthorName = !isOwn && !grouped && !isSystem && spaceType !== "dm";

  const bubbleBg = isDeleted
    ? isOwn
      ? alpha(theme.palette.primary.main, 0.72)
      : theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.06)
        : alpha(theme.palette.grey[200], 0.85)
    : isSystem
    ? "action.hover"
    : isOwn
      ? theme.palette.primary.main
      : theme.palette.mode === "dark"
        ? alpha(theme.palette.common.white, 0.1)
        : theme.palette.background.paper;

  const bubbleRadius = isOwn ? "18px 18px 4px 18px" : "18px 18px 18px 4px";
  const bubbleShadow = isOwn
    ? `0 1px 1px ${alpha(theme.palette.common.black, 0.1)}`
    : theme.palette.mode === "dark"
      ? "none"
      : `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`;

  const handlePickEmoji = (emoji) => {
    setEmojiAnchor(null);
    onReact?.(msg, emoji);
  };

  const handleQuotedNavigate = (variant) => {
    if (typeof onNavigateToQuotedMessage !== "function") return;
    const target = resolveTeamQuotedNavigationTarget({
      replyPreview: variant === "reply" ? msg.replyPreview : null,
      replyToId: variant === "reply" ? msg.replyToId : null,
      forwardedFrom: variant === "forward" ? forwarded : null,
      spaceId: currentSpaceId ?? msg.spaceId,
    });
    if (target) onNavigateToQuotedMessage(target);
  };

  const canNavigateReply =
    typeof onNavigateToQuotedMessage === "function" &&
    (msg.replyPreview?.id != null || msg.replyToId != null);
  const canNavigateForward =
    typeof onNavigateToQuotedMessage === "function" &&
    forwarded?.messageId != null;

  const hoverActions = !isSystem && !isDeleted && !isPendingOrFailed ? (
    <Box
      className="team-msg-actions"
      sx={{
        ...(isCompactActions
          ? {
              position: "static",
              transform: "none",
              display: "flex",
              alignItems: "center",
              opacity: 1,
              pointerEvents: "auto",
              mt: 0.35,
              mb: 0,
              bgcolor: "transparent",
              boxShadow: "none",
              border: "none",
              p: 0,
            }
          : {
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              ...(isOwn ? { right: "calc(100% + 6px)" } : { left: "calc(100% + 6px)" }),
              display: "flex",
              alignItems: "center",
              bgcolor: alpha(theme.palette.background.paper, 0.98),
              borderRadius: 10,
              boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.12)}`,
              border: "none",
              opacity: hovered ? 1 : 0,
              pointerEvents: hovered ? "auto" : "none",
              transition: "opacity 0.14s ease",
              p: 0.25,
              "@media (hover: none)": {
                opacity: 0.85,
                pointerEvents: "auto",
              },
            }),
        zIndex: 3,
      }}
    >
      <Tooltip title={t("actionReply")} placement="top">
        <IconButton size="small" onClick={() => onReply?.(msg)} aria-label={t("actionReply")}>
          <ReplyIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("actionReact")} placement="top">
        <IconButton
          size="small"
          onClick={(e) => setEmojiAnchor(e.currentTarget)}
          aria-label={t("actionReact")}
        >
          <AddReactionOutlinedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("actionForward")} placement="top">
        <IconButton size="small" onClick={() => onForward?.(msg)} aria-label={t("actionForward")}>
          <ForwardIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      {canEdit ? (
        <Tooltip title={t("actionEdit")} placement="top">
          <IconButton size="small" onClick={() => onEdit?.(msg)} aria-label={t("actionEdit")}>
            <EditOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      ) : null}
      {canDelete ? (
        <Tooltip title={t("actionDelete")} placement="top">
          <IconButton size="small" onClick={() => onDelete?.(msg)} aria-label={t("actionDelete")}>
            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  ) : null;

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex",
        gap: 0.75,
        mb: grouped ? 0.35 : 1.25,
        flexDirection: isOwn ? "row-reverse" : "row",
        alignItems: "flex-end",
        px: 0.25,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      {!isOwn && !grouped ? (
        isAgent || isChatbot ? (
          <Avatar sx={{ width: 32, height: 32, bgcolor: "secondary.main", mb: 0.15 }}>
            <SmartToyOutlinedIcon sx={{ fontSize: 17 }} />
          </Avatar>
        ) : (
          <TeamPresenceAvatar
            userId={msg.authorId}
            src={msg.authorAvatarUrl}
            name={authorLabel}
            size={32}
            showDot={false}
          />
        )
      ) : (
        <Box sx={{ width: 32, flexShrink: 0 }} />
      )}

      <Box
        sx={{
          maxWidth: isCompactActions ? "min(88%, 540px)" : "min(78%, 540px)",
          display: "flex",
          flexDirection: "column",
          alignItems: isOwn ? "flex-end" : "flex-start",
          minWidth: 0,
          position: "relative",
        }}
      >
        {showAuthorName ? (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "text.secondary",
              mb: 0.25,
              ml: 0.5,
              fontSize: "0.75rem",
              lineHeight: 1.2,
            }}
          >
            {authorLabel}
          </Typography>
        ) : null}

        {hasBubbleBody ? (
          <Box
            sx={{
              position: "relative",
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: isOwn ? "flex-end" : "flex-start",
            }}
          >
            {!isCompactActions ? hoverActions : null}
            <Box
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: bubbleRadius,
                bgcolor: bubbleBg,
                color:
                  isOwn && !isSystem
                    ? "primary.contrastText"
                    : "text.primary",
                border: "none",
                boxShadow: bubbleShadow,
                maxWidth: "100%",
                position: "relative",
                ...(showYoutubeEmbeds
                  ? {
                      width: "100%",
                      minWidth: { xs: 220, sm: 280 },
                    }
                  : {}),
              }}
            >
              {forwarded ? (
                <TeamQuotedBlock
                  variant="forward"
                  authorName={forwarded.authorName}
                  message={forwarded}
                  isOwn={isOwn}
                  onClick={canNavigateForward ? () => handleQuotedNavigate("forward") : undefined}
                />
              ) : null}
              {msg.replyPreview ? (
                <TeamQuotedBlock
                  variant="reply"
                  authorName={msg.replyPreview.authorName}
                  message={msg.replyPreview}
                  isOwn={isOwn}
                  onClick={canNavigateReply ? () => handleQuotedNavigate("reply") : undefined}
                />
              ) : null}

              {showVideoCallArtifact && !isDeleted ? (
                <Box sx={{ maxWidth: "100%" }}>
                  <VideoCallSummaryMessage
                    message={msg}
                    variant="team"
                    embedded
                    bubbleFg={isOwn ? "inherit" : undefined}
                    onAccentBubble={isOwn}
                  />
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                    <MessageMetaFooter
                      msg={msg}
                      isOwn={isOwn}
                      spaceType={spaceType}
                      theme={theme}
                      t={t}
                      onShowEditHistory={
                        canViewEditHistory ? () => setEditHistoryOpen(true) : undefined
                      }
                    />
                  </Box>
                </Box>
              ) : showVideoInvite && !isDeleted ? (
                <Box sx={{ maxWidth: "100%" }}>
                  <VideoInviteMessage
                    message={msg}
                    bubbleFg={isOwn ? "inherit" : undefined}
                    onAccentBubble={isOwn}
                  />
                  {!hasMediaBelow ? (
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                      <MessageMetaFooter
                        msg={msg}
                        isOwn={isOwn}
                        spaceType={spaceType}
                        theme={theme}
                        t={t}
                        onShowEditHistory={
                          canViewEditHistory ? () => setEditHistoryOpen(true) : undefined
                        }
                      />
                    </Box>
                  ) : null}
                </Box>
              ) : showText ? (
                <Typography
                  component="div"
                  variant="body2"
                  sx={{
                    lineHeight: 1.45,
                    fontSize: "0.9375rem",
                    wordBreak: "break-word",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      ...(isDeleted
                        ? {
                            fontStyle: "italic",
                            opacity: 0.88,
                            fontSize: "0.875rem",
                          }
                        : {}),
                    }}
                  >
                    {isDeleted ? (
                      deletedPlaceholder
                    ) : isSystem ? (
                      <TeamSystemMessageText
                        content={msg.content}
                        leadId={msg.leadId ?? spaceLeadId}
                        cardId={msg.metadata?.cardId ?? spaceCardId}
                        onOpenTask={onOpenTask}
                      />
                    ) : (
                      <TeamLinkifiedText
                        content={msg.content}
                        onAccentBubble={isOwn}
                      />
                    )}
                  </Box>
                  {!hasMediaBelow ? (
                    <MessageMetaFooter
                      msg={msg}
                      isOwn={isOwn}
                      spaceType={spaceType}
                      theme={theme}
                      t={t}
                      onShowEditHistory={
                        canViewEditHistory ? () => setEditHistoryOpen(true) : undefined
                      }
                    />
                  ) : null}
                </Typography>
              ) : null}

              {showYoutubeEmbeds ? (
                <Box
                  sx={{
                    mt: showText || msg.replyPreview || forwarded || showVideoInvite ? 0.75 : 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    width: "100%",
                  }}
                >
                  {youtubeVideos.map((video) => (
                    <TeamYouTubeEmbed
                      key={video.videoId}
                      video={video}
                      onAccentBubble={isOwn}
                    />
                  ))}
                </Box>
              ) : null}

              {hasAttachment && !showVideoCallArtifact ? (
                <Box
                  sx={{
                    mt: showText || msg.replyPreview || forwarded || showYoutubeEmbeds ? 0.5 : 0,
                    maxWidth: "100%",
                  }}
                >
                  <TeamMessageAttachment
                    message={msg}
                    isOwn={isOwn}
                    onAudioPlayed={onAudioPlayed}
                  />
                </Box>
              ) : null}

              {(hasMediaBelow && !showVideoCallArtifact) ||
              (!showText && !showVideoCallArtifact && (msg.replyPreview || forwarded)) ? (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.35, minHeight: 14 }}>
                  <MessageMetaFooter
                    msg={msg}
                    isOwn={isOwn}
                    spaceType={spaceType}
                    theme={theme}
                    t={t}
                    onShowEditHistory={
                      canViewEditHistory ? () => setEditHistoryOpen(true) : undefined
                    }
                  />
                </Box>
              ) : null}
            </Box>
            {isCompactActions ? hoverActions : null}
          </Box>
        ) : null}

        {hasBubbleBody ? (
          <TeamMessageReactions
            reactions={msg.reactions}
            currentUserId={currentUserId}
            onToggle={(emoji) => onReact?.(msg, emoji)}
            isOwn={isOwn}
          />
        ) : null}

        {showInteractive ? (
          <TeamChatbotInteractive
            metadata={msg.metadata}
            disabled={isSending || !onQuickReply}
            onSelect={(label) => onQuickReply?.(msg.id, label)}
          />
        ) : null}

        {!hasBubbleBody && hasReactions ? (
          <TeamMessageReactions
            reactions={msg.reactions}
            currentUserId={currentUserId}
            onToggle={(emoji) => onReact?.(msg, emoji)}
            isOwn={isOwn}
          />
        ) : null}
      </Box>

      <TeamReactionPicker
        anchorEl={emojiAnchor}
        open={Boolean(emojiAnchor)}
        onClose={() => setEmojiAnchor(null)}
        onSelectEmoji={handlePickEmoji}
      />

      <TeamMessageEditHistoryDialog
        open={editHistoryOpen}
        onClose={() => setEditHistoryOpen(false)}
        message={msg}
      />
    </Box>
  );
};

export default TeamMessageBubble;
